import { Injectable } from '@angular/core';
import { StorageManagerProvider } from '../storage-manager/storage-manager';
import { Events } from '../events/events';
import { HttpClient } from '@angular/common/http';
import confetti from 'canvas-confetti';
import { LoggerService } from '../../app/services/logger.service';
@Injectable()
export class AdsService {
  async showRewardAdIfRequested(key: string, conditionNumber: number) {
    const storedRequestedTimes = await this.storageManager.get("ads-"+key);
    if (!storedRequestedTimes) {
      this.storageManager.set("ads-"+key, 0);
    } else {
      if(storedRequestedTimes < conditionNumber) {
        this.storageManager.set("ads-"+key, storedRequestedTimes + 1);
      }
      if(storedRequestedTimes%conditionNumber===0) 
        this.showRewardAd();
      }
  }
  
  // Store ad request timestamps as an array of numbers (milliseconds since epoch)
  requestedTimes: number[] = [];

  // Property to track shown ad timestamps
  shownAdsTimestamps: number[] = [];

  public static allowAd: boolean = true;
  public maxAdsPerHour: number = 3; // Max ads allowed per hour
  public static adsTimePeriod = 90; // Not used in current context
  private adsNeeded: boolean = false;

  constructor(
    public http: HttpClient,
    public storageManager: StorageManagerProvider,
    public events: Events,
    public logger: LoggerService
  ) {}

  /**
   * Initialize the AdsService by loading stored data.
   */
  async init() {
    // Initialize requestedTimes
    const storedRequestedTimes = await this.storageManager.get("requestedTimes");
    if (storedRequestedTimes && Array.isArray(storedRequestedTimes)) {
      this.requestedTimes = storedRequestedTimes;
    } else {
      this.requestedTimes = [];
    }

    // Initialize shownAdsTimestamps
    const storedShownAdsTimestamps = await this.storageManager.get("shownAdsTimestamps");
    if (storedShownAdsTimestamps && Array.isArray(storedShownAdsTimestamps)) {
      this.shownAdsTimestamps = storedShownAdsTimestamps;
    } else {
      this.shownAdsTimestamps = [];
    }
  }

  /**
   * Handle app resume event.
   */
  onResume() {
    AdsService.allowAd = true;
    this.logger.debug("allow ad", AdsService.allowAd);
    // Initialize and show ads as needed
  }

  /**
   * Handle app pause event.
   */
  onPause() {
    AdsService.allowAd = false;
    this.logger.debug("allow ad", AdsService.allowAd);
  }

  /**
   * Prepare a rewarded ad.
   * @returns Always returns false in this implementation.
   */
  prepareRewardAd(): false | undefined {
    return false;
  }

  /**
   * Set whether ads are needed.
   * @param isNeeded Boolean indicating if ads are needed.
   */
  setAdsNeeded(isNeeded: boolean) {
    this.adsNeeded = isNeeded;
    this.prepareRewardAd();
  }

  /**
   * Show a rewarded ad with an optional delay.
   * @param timeDelay Delay in milliseconds before showing the ad.
   */
  async showRewardAd(timeDelay: number = 5000) {
    await this.increaseRequestedTimes();

    if (this.allowAd()) {
      await this.increaseShownAdsNumber();
      setTimeout(() => {
        //@ts-ignore
        show_8485321()
          .then(() => {
            // Handle ad shown successfully
            confetti({
              particleCount: 400,
              spread: 220,
              origin: { y: 0.6 },
            });
            this.logger.debug("Ad shown successfully.");
          })
          .catch((err: any) => {
            console.error("Error showing ad:", err);
          });
      }, timeDelay);
    } else {
      this.logger.debug("Ad not allowed: hourly request limit reached or other conditions not met.");
    }
  }

  /**
   * Increment the count of requested ads by storing the current timestamp.
   */
  async increaseRequestedTimes() {
    const storedRequestedTimes = await this.storageManager.get("requestedTimes");
    const currentTime = Date.now();

    if (storedRequestedTimes && Array.isArray(storedRequestedTimes)) {
      // Filter out timestamps older than 1 hour (3600000 ms)
      this.requestedTimes = storedRequestedTimes.filter(
        (timestamp: number) => currentTime - timestamp < 3600000
      );
    } else {
      this.requestedTimes = [];
    }

    // Add the current timestamp
    this.requestedTimes.push(currentTime);

    // Save the updated timestamps back to storage
    await this.storageManager.set("requestedTimes", this.requestedTimes);
  }

  /**
   * Increment the count of shown ads by storing the current timestamp.
   */
  async increaseShownAdsNumber() {
    const currentTime = Date.now();

    // Retrieve stored shownAdsTimestamps
    const storedShownAdsTimestamps = await this.storageManager.get("shownAdsTimestamps");
    if (storedShownAdsTimestamps && Array.isArray(storedShownAdsTimestamps)) {
      // Filter out timestamps older than 1 hour (3600000 ms)
      this.shownAdsTimestamps = storedShownAdsTimestamps.filter(
        (timestamp: number) => currentTime - timestamp < 3600000
      );
    } else {
      this.shownAdsTimestamps = [];
    }

    // Add the current timestamp
    this.shownAdsTimestamps.push(currentTime);
    await this.storageManager.set("shownAdsTimestamps", this.shownAdsTimestamps);
  }

  /**
   * Determine whether an ad is allowed to be shown based on the hourly limit and global allowance.
   * @returns Boolean indicating if an ad can be shown.
   */
  allowAd(): boolean {
    const currentTime = Date.now();
    const oneHourAgo = currentTime - 3600000; // 1 hour in milliseconds

    // Ensure shownAdsTimestamps is up-to-date by removing old timestamps
    this.shownAdsTimestamps = this.shownAdsTimestamps.filter(
      (timestamp: number) => timestamp >= oneHourAgo
    );

    // Update the stored shownAdsTimestamps after filtering
    this.storageManager.set("shownAdsTimestamps", this.shownAdsTimestamps);

    // Allow ad if:
    // 1. Shown ads in the last hour is less than maxAdsPerHour
    // 2. Ads are allowed globally
    const canShow =
      this.shownAdsTimestamps.length < this.maxAdsPerHour &&
      AdsService.allowAd;

    this.logger.debug(
      `Ad allowed: ${canShow} | Shown ads in last hour: ${this.shownAdsTimestamps.length}`
    );

    return canShow;
  }
}
